import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, ref, onMounted, nextTick } from "vue";
import { gsap } from "gsap";

import { Scatter } from "vue-chartjs";
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Tooltip, Legend } from "chart.js";
import ListLegend from "~/vue/molecules/lists/ListLegend.vue";

import { getClasses } from "~/js/composables/classes";

interface Dataset {
	label: string;
	values: Array<Number>;
	color: string;
}

interface Props {
	dataset: Array<Dataset> | null;
	utilities?: string;
	options?: any;
	animate?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GraphScatter',
  props: {
    dataset: {},
    utilities: { default: "" },
    options: { default: null },
    animate: { type: Boolean, default: false }
  },
  setup(__props: any) {

// https://vue-chartjs.org/guide/
ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

const props = __props;

const classes = ref({
	wrapper: {
		aspect: "aspect-[2]",
		utilities: props.utilities,
	},
});
const wrapper = ref(null);
const chart = ref(null);

const chartData = computed(() => {
	return {
		datasets: props.dataset.map((set, i) => {
			return {
				backgroundColor: set.color,
				pointRadius: 8,
				pointHoverRadius: 8,
				...set,
			};
		}),
	};
});

const chartOptions = computed(() => {
	return {
		plugins: {
			axes: {
				labels: {
					// This more specific font property overrides the global property
					font: {
						family: "'Roboto', 'sans-serif'",
						size: 18,
					},
				},
			},
			backgroundBar: false,
			legend: {
				display: false,
			},
			tooltip: {
				callbacks: {
					label: function (context) {
						return `${context.dataset.data[context.dataIndex].label}`;
					},
				},
			},
		},
		responsive: true,
		scales: {
			x: {
				ticks: {
					font: {
						family: "'Roboto', 'sans-serif'",
						size: 14,
					},
				},
				grid: {
					display: false,
				},
				...props.options?.scales?.x,
			},
			y: {
				ticks: {
					font: {
						family: "'Roboto', 'sans-serif'",
						size: 14,
					},
				},
				grid: {
					color: "#f9f9f9",
				},
				...props.options?.scales?.y,
			},
		},
	};
});

const chartLegend = computed(() => {
	return props.dataset.map((set) => {
		return {
			label: set.label,
			color: set.color,
		};
	});
});

onMounted(() => {
	if (props.animate) {
		nextTick(() => {
			gsap.from(wrapper.value, {
				scrollTrigger: {
					trigger: wrapper.value,
					start: "-150% top",
					end: "0 top",
				},
				onStart: () => {
					const data = chart.value.chart.data.datasets.map((set) => set.data);
					chart.value.chart.data.datasets.forEach((_, i) => {
						chart.value.chart.data.datasets[i].data = null;
					});
					chart.value.chart.update();
					chart.value.chart.data.datasets.forEach((_, i) => {
						chart.value.chart.data.datasets[i].data = data[i];
					});
					chart.value.chart.update();
				},
				opacity: 0,
			});
		});
	}
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("figure", {
    ref_key: "wrapper",
    ref: wrapper,
    class: _normalizeClass(_unref(getClasses)(classes.value, 'wrapper'))
  }, [
    _createVNode(_unref(Scatter), {
      ref_key: "chart",
      ref: chart,
      data: chartData.value,
      options: chartOptions.value,
      class: "w-full"
    }, null, 8, ["data", "options"]),
    _createVNode(ListLegend, { legend: chartLegend.value }, null, 8, ["legend"])
  ], 2))
}
}

})